import { CareDomain } from '../../insights/IInsight';
import type { IAgencyClient, IAgencyDigest, IAgencyFinding } from './agencyDigest';
import { classifyFinding, mentionsAny } from './agencyFindingKind';
import { comparableClientIds } from './agencyComparable';
import { STANDING_LABEL } from './agencyChartTheme';
import { RECENT_WEEKS } from './agencyThresholds';

/**
 * The six everyday things a caregiver notices about a person, and how far each has moved.
 *
 * These are deliberately not clinical categories. A case manager does not think "the client has a
 * nutrition deficit", they think "she's not been eating". The names are the words someone would use
 * out loud, because the reader of this is a dispatcher deciding who to visit, not a clinician
 * writing a note.
 *
 * A finding carries the parts of daily life the model said it was about. One without that field
 * (written before it existed, or a sample) is matched on its words instead, which is a hint and
 * nothing more. Either way the score is a way of ordering a caseload and sizing a wedge, not a
 * measurement of a person: it counts what has been written down recently, so it can say "more has
 * been raised here than usual" and cannot say how anyone actually is.
 */

/** Clockwise from the top of the hexagon, which is the order the diagram draws them in. */
export const DOMAIN_ORDER: CareDomain[] = ['rest', 'eating', 'mood', 'comfort', 'connection', 'clarity'];

export const DOMAIN_LABEL: { [key in CareDomain]: string } = {
    rest: 'Rest',
    eating: 'Eating',
    mood: 'Mood',
    comfort: 'Comfort',
    connection: 'Connection',
    clarity: 'Clarity'
};

/**
 * Words that place a finding with no domains field. A finding can land in more than one, which is
 * correct: "too tired to see anyone" really is about rest and about connection, and forcing a single
 * winner would lose half of what the sentence says.
 *
 * Every term here has to be specific enough that it cannot be triggered by a sentence about
 * something else. Bare "down" put "appetite down" into low mood, and "call" put "ambulance
 * called" into social connection, which is how a keyword classifier quietly turns a fall into a
 * loneliness problem. Prefer a longer phrase over a short word that happens to appear.
 */
const DOMAIN_TERMS: { [key in CareDomain]: string[] } = {
    rest: ['sleep', 'slept', 'sleeping', 'restless', 'insomnia', 'tired', 'fatigue', 'exhausted', 'woke', 'waking', 'bedtime', 'napping'],
    eating: ['eat', 'ate', 'appetite', 'meal', 'food', 'weight', 'nutrition', 'hydrat', 'swallow'],
    mood: ['mood', 'spirits', 'sad', 'anxious', 'anxiety', 'depress', 'tearful', 'agitated', 'withdrawn', 'irritable'],
    comfort: ['pain', 'ache', 'sore', 'discomfort', 'fall', 'fell', 'unsteady', 'dizzy', 'breath', 'nausea', 'injury', 'bruise', 'medication', 'blood pressure'],
    // Not bare "visit": an ER visit is not a social one, and tagging it as connection would
    // report a hospital trip as loneliness.
    connection: ['visitor', 'visiting', 'fewer visit', 'social visit', 'family', 'friend', 'alone', 'lonely', 'social', 'church', 'outing', 'isolat'],
    clarity: ['confus', 'memory', 'forgot', 'forget', 'disorient', 'cogniti', 'lucid', 'wander']
};

/** What a finding's wording suggests it is about, for a finding with no domains field. */
const domainsFromWording = (finding: IAgencyFinding): CareDomain[] => {
    const text = `${finding.title} ${finding.narrative}`;
    // Word-start matching, not substring: "eat" appears inside "treatment" and "created", either of
    // which would otherwise file a medication note under how well someone is eating.
    return DOMAIN_ORDER.filter(domain => mentionsAny(text, DOMAIN_TERMS[domain]));
};

/** Which domains a finding speaks to, in drawing order. Empty when it speaks to none of them. */
export const domainsOf = (finding: IAgencyFinding): CareDomain[] => {
    const assigned = finding.domains;
    if (assigned === undefined) {
        return domainsFromWording(finding);
    }
    return DOMAIN_ORDER.filter(domain => assigned.includes(domain));
};

/** The band a score falls in, which is what the pill and the sorting use. */
export type DomainState = 'usual' | 'drifting' | 'sharp';

/**
 * A client's overall reading. 'unscored' is the absence of one rather than a fourth band, and sits in
 * the union for the reason BaselineStanding's 'unknown' does: every label and colour map has to name it.
 * Who is unscored is decided in comparableClientIds.
 */
export type OverallState = DomainState | 'unscored';

/** Why a client cannot be compared, which changes what a reader does: chase the record, or wait. */
export type UnscoredReason = 'little-history' | 'nothing-recent';

/**
 * Everything starts at a hundred and comes down as things get raised.
 *
 * A hundred is "nothing has been raised", not "verified well". One note takes a domain out of the
 * steady band without making it urgent, because findings are sparse and treating a single note as a
 * deterioration is how a tool starts crying wolf. Something that already happened is worth more than
 * two ordinary notes on its own: a fall does not need a second sighting to be worth a visit.
 */
export const FULL_SCORE = 100;
const PER_FINDING = 22;
const PER_INCIDENT = 45;

/** Where the bands sit on the scale. A pill and a wedge colour must never disagree about these. */
export const STEADY_AT_OR_ABOVE = 85;
export const WORTH_A_LOOK_AT_OR_ABOVE = 60;

/** Extra copies of the worst part of life in the overall average. See weightedScore. */
const WORST_WEIGHT = 3;

export const stateForScore = (score: number): DomainState => {
    if (score >= STEADY_AT_OR_ABOVE) {
        return 'usual';
    }
    if (score >= WORTH_A_LOOK_AT_OR_ABOVE) {
        return 'drifting';
    }
    return 'sharp';
};

/** One thing that was written down, kept whole so the detail view can show the actual words. */
export interface IDomainNote {
    title: string;
    narrative: string;
    observedOn: string;
    isIncident: boolean;
}

export interface IDomainReading {
    domain: CareDomain;
    /** 0 to 100. A hundred means nothing has been raised about this part of life recently. */
    score: number;
    state: DomainState;
    /** The notes behind the score, most recent first, so the detail view can say why. */
    notes: IDomainNote[];
}

interface IWellbeingReading {
    careMapId: string;
    clientName: string;
    readings: IDomainReading[];
    /** Weighted toward the worst part, so a caseload can be ordered by one number. */
    score: number;
    /** Domains that have moved, worst first, for the sentence under the diagram. */
    moved: CareDomain[];
    /** Incidents recorded against no part of daily life, which no wedge can show. */
    unplacedIncidents: IDomainNote[];
}

/** A reading with a band. */
export interface IScoredWellbeing extends IWellbeingReading {
    /** Taken from the worst part, not from the average: five quiet parts must not hide a fall. */
    overall: DomainState;
}

/** A client comparableClientIds leaves out. The score and every reading's state are for ordering only. */
export interface IUnscoredWellbeing extends IWellbeingReading {
    overall: 'unscored';
    unscoredBecause: UnscoredReason;
}

export type IClientWellbeing = IScoredWellbeing | IUnscoredWellbeing;

export const asUnscored = (params: { wellbeing: IScoredWellbeing; recordedRecently: boolean }): IUnscoredWellbeing => {
    const { wellbeing, recordedRecently } = params;
    return {
        ...wellbeing,
        overall: 'unscored',
        unscoredBecause: recordedRecently ? 'little-history' : 'nothing-recent',
        moved: []
    };
};

const SEVERITY: { [key in DomainState]: number } = { usual: 0, drifting: 1, sharp: 2 };

const toDomainNote = (finding: IAgencyFinding): IDomainNote => ({
    title: finding.title,
    narrative: finding.narrative,
    observedOn: finding.observedOn,
    isIncident: classifyFinding(finding) === 'incident'
});

const mostRecentFirst = (a: IAgencyFinding, b: IAgencyFinding): number => b.observedOn.localeCompare(a.observedOn);

/**
 * Weighted toward the worst part of life rather than a flat mean. A flat mean buries the thing the
 * reader needs: one part collapsing to 11 still averaged 85 across six, which reads as a Steady week
 * sitting under a High Alert pill. Counting the worst three times keeps the number and the pill
 * telling the same story, while a spread across several parts still pulls it down.
 */
const weightedScore = (scores: number[]): number => {
    const worstScore = Math.min(...scores);
    const scoreTotal = scores.reduce((running, one) => running + one, 0);
    return Math.round((worstScore * WORST_WEIGHT + scoreTotal) / (scores.length + WORST_WEIGHT));
};

export const buildClientWellbeing = (params: {
    careMapId: string;
    clientName: string;
    findings: IAgencyFinding[];
    /** Only findings at or after this moment; the caller passes the recent window the tab uses. */
    since: Date;
    /**
     * Exclusive end of the window. Omitted for the tab's own diagrams, which run to now. Passed when
     * reading one week at a time, which is how a client's week-to-week direction is established.
     */
    until?: Date;
}): IScoredWellbeing => {
    const { careMapId, clientName, findings, since, until } = params;

    const recent = findings.filter(finding => {
        const at = new Date(finding.observedOn).getTime();
        if (finding.careMapId !== careMapId || isNaN(at) || at < since.getTime()) {
            return false;
        }
        return until === undefined || at < until.getTime();
    });

    const readings: IDomainReading[] = DOMAIN_ORDER.map(domain => {
        const notes = recent
            .filter(finding => domainsOf(finding).includes(domain))
            .sort(mostRecentFirst)
            .map(toDomainNote);

        const deduction = notes.reduce((running, note) => running + (note.isIncident ? PER_INCIDENT : PER_FINDING), 0);
        const domainScore = Math.max(0, FULL_SCORE - deduction);

        return { domain, score: domainScore, state: stateForScore(domainScore), notes };
    });

    const moved = readings
        .filter(reading => reading.state !== 'usual')
        .sort((a, b) => a.score - b.score)
        .map(reading => reading.domain);

    const unplacedIncidents = recent
        .filter(finding => domainsOf(finding).length === 0 && classifyFinding(finding) === 'incident')
        .sort(mostRecentFirst)
        .map(toDomainNote);

    // The baseline flags any recent incident, whatever part of life it names, so this must not read
    // that week as settled. With no wedge to show it, it reads as one more part of life.
    const unplacedScores = unplacedIncidents.length > 0
        ? [Math.max(0, FULL_SCORE - unplacedIncidents.length * PER_INCIDENT)]
        : [];
    const scores = [...readings.map(reading => reading.score), ...unplacedScores];
    const overall = scores.map(stateForScore).reduce<DomainState>(
        (worstSoFar, state) => (SEVERITY[state] > SEVERITY[worstSoFar] ? state : worstSoFar),
        'usual'
    );

    return { careMapId, clientName, readings, score: weightedScore(scores), overall, moved, unplacedIncidents };
};

/** Said wherever a reading is described, so a High Alert with every wedge full is never unexplained. */
export const UNPLACED_INCIDENT_SENTENCE = 'An incident was recorded that is not tied to any one part of daily life.';

/**
 * The sentence under a client's diagram. Names what moved, because a colour alone is not a handover.
 *
 * "Decreased from baseline" rather than a figure of speech: this sits next to a number out of a
 * hundred, and the reader has to be able to tell which direction the number went without decoding
 * an idiom.
 */
export const describeWellbeing = (wellbeing: IClientWellbeing): string => {
    if (wellbeing.overall === 'unscored') {
        return wellbeing.unscoredBecause === 'little-history'
            ? 'Not enough history yet to compare against their own baseline.'
            : 'Nothing recent on record to compare against their own baseline.';
    }

    const hasUnplaced = wellbeing.unplacedIncidents.length > 0;
    if (wellbeing.moved.length === 0) {
        return hasUnplaced ? UNPLACED_INCIDENT_SENTENCE : 'Settled. About like they usually are.';
    }

    const named = wellbeing.moved.slice(0, 2).map(domain => DOMAIN_LABEL[domain].toLowerCase());
    const rest = wellbeing.moved.length - named.length;
    const list = named.length === 2 ? `${named[0]} and ${named[1]}` : named[0];
    const tail = rest > 0 ? `, and ${rest} more` : '';
    const verb = named.length === 2 || rest > 0 ? 'have' : 'has';

    const sentence = `${list.charAt(0).toUpperCase()}${list.slice(1)}${tail} ${verb} decreased from baseline.`;
    return hasUnplaced ? `${sentence} ${UNPLACED_INCIDENT_SENTENCE}` : sentence;
};

/** The one-line explanation of the whole diagram, shown as a tooltip rather than a block of text. */
export const WELLBEING_EXPLAINER =
    'Each wedge is one part of daily life, scored out of 100 from what has been recorded in the last '
    + 'few weeks. A wedge reaches the dashed circle when nothing has been raised about it, which means '
    + 'nobody has raised it, not that somebody has checked. Until there is enough to compare someone '
    + 'against their own baseline, only the pale tracks are drawn, with no number.';

/** The words on the pill under the diagram. */
export const OVERALL_LABEL: { [key in OverallState]: string } = {
    usual: 'Steady',
    drifting: 'Worth a Look',
    sharp: 'High Alert',
    unscored: STANDING_LABEL.unknown
};

/**
 * Every client's reading, worst first.
 *
 * Ordered by the overall band and then by the score, so one sharp change outranks
 * several mild ones. That is the right way round for a visit list: a fall beats a run of quiet notes,
 * however many of them there are.
 */
export const buildCaseloadWellbeing = (params: { digest: IAgencyDigest }): IClientWellbeing[] => {
    const { digest } = params;
    const since = new Date(`${digest.recentStartsOn}T00:00:00.000Z`);
    const comparable = comparableClientIds(digest);
    const recordedRecently = new Set(
        digest.baselines.filter(baseline => baseline.volume.recordedRecently).map(baseline => baseline.careMapId)
    );

    const all = digest.clients.map((client: IAgencyClient): IClientWellbeing => {
        const wellbeing = buildClientWellbeing({
            careMapId: client.careMapId,
            clientName: client.clientName,
            findings: digest.recentFindings,
            since
        });
        return comparable.has(client.careMapId)
            ? wellbeing
            : asUnscored({ wellbeing, recordedRecently: recordedRecently.has(client.careMapId) });
    });

    // Unscored last: a client nothing can be said about is no place to start a visit list.
    const rank = (wellbeing: IClientWellbeing): number => (wellbeing.overall === 'unscored' ? -1 : SEVERITY[wellbeing.overall]);
    return all.sort((a, b) => (rank(b) - rank(a)) || (a.score - b.score));
};

const WEEK_MS = 7 * 24 * 60 * 60 * 1000;

/** One client's reading in one week of the window. */
export interface IWeeklyStanding {
    weekStartsOn: string;
    state: DomainState;
    score: number;
    /** Parts of daily life outside their usual band that week, worst first. */
    moved: CareDomain[];
}

/** A span of time in epoch milliseconds, from inclusive and to exclusive. */
export interface IWeekSpan {
    from: number;
    to: number;
}

/**
 * Rolling seven-day spans ending at the read, newest first.
 *
 * Rolling rather than the calendar buckets the chart is drawn on, and the difference matters. The
 * current calendar bucket is a few hours old on a Monday, so "is this client steady now" read as yes
 * for everybody at the start of every week and the figures lurched as the week went on. A span that
 * always covers the last seven days answers the same question the same way whatever day it is asked.
 *
 * The newest span includes the moment of the read, because the digest keeps a finding stamped then.
 */
export const rollingWeeksTo = (params: { readAt: number; count: number }): IWeekSpan[] => {
    const { readAt, count } = params;
    return Array.from({ length: count }, (unused, index) => {
        const to = readAt - index * WEEK_MS;
        return { from: to - WEEK_MS, to: index === 0 ? to + 1 : to };
    });
};

/**
 * A client's week-by-week reading across the recent window, for the Status page's Over Time card.
 *
 * Read one week at a time rather than cumulatively, because the question it answers is about
 * direction: a cumulative reading can only ever get worse as findings pile up, so a client who
 * recovered would look identical to one who never did.
 */
export const buildWeeklyStandings = (params: {
    digest: IAgencyDigest;
    careMapId: string;
    clientName: string;
}): IWeeklyStanding[] => {
    const { digest, careMapId, clientName } = params;

    const oldestFirst = rollingWeeksTo({ readAt: new Date(digest.readAt).getTime(), count: RECENT_WEEKS }).reverse();
    return oldestFirst.map(({ from, to }) => {
        const wellbeing = buildClientWellbeing({
            careMapId,
            clientName,
            findings: digest.windowFindings,
            since: new Date(from),
            until: new Date(to)
        });

        return {
            weekStartsOn: new Date(from).toISOString().slice(0, 10),
            state: wellbeing.overall,
            score: wellbeing.score,
            moved: wellbeing.moved
        };
    });
};
